"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectAppPageRowVisibility = projectAppPageRowVisibility;
exports.appPageOperationWrites = appPageOperationWrites;
exports.appBaseRowOwnershipRequirements = appBaseRowOwnershipRequirements;
const app_page_access_1 = require("./app-page-access");
/** The intersection of admitted and current binding policies, admitted and
 * ruled per table by the caller. */
function projectRowVisibility(input) {
    const { binding, policies } = input;
    const current = (target) => {
        const policy = policies[target.sourceKey];
        return policy?.status === 'active' &&
            policy.databaseId === target.databaseId
            ? policy
            : null;
    };
    const intersect = (admitted) => {
        const policy = current(admitted);
        if (!policy ||
            !input.admits(admitted, policy) ||
            (input.anonymous &&
                (admitted.exposure.columnIds.length === 0 ||
                    policy.exposure.columnIds.length === 0)))
            return null;
        const oldIds = admitted.exposure.columnIds;
        const liveIds = policy.exposure.columnIds;
        const columnIds = oldIds.length === 0
            ? liveIds
            : liveIds.length === 0
                ? oldIds
                : oldIds.filter(id => liveIds.includes(id));
        if (oldIds.length > 0 && liveIds.length > 0 && columnIds.length === 0)
            return null;
        return {
            ...admitted,
            exposure: { ...admitted.exposure, columnIds },
        };
    };
    // Denial on any alias applies to the physical database. Removing just the
    // denied alias would accidentally make its permissive sibling authoritative.
    const deniedDatabases = new Set(Object.values(input.bindings)
        .filter(admitted => !intersect(admitted))
        .map(admitted => admitted.databaseId));
    const projectedBinding = deniedDatabases.has(binding.databaseId)
        ? null
        : intersect(binding);
    if (!projectedBinding)
        return null;
    const projectedBindings = Object.fromEntries(Object.entries(input.bindings).flatMap(([key, admitted]) => {
        const next = intersect(admitted);
        return next && !deniedDatabases.has(admitted.databaseId)
            ? [[key, next]]
            : [];
    }));
    const modesFor = (admitted) => {
        const target = current(admitted);
        return target ? input.modesOf(target) : ['unavailable'];
    };
    return { projectedBinding, projectedBindings, modesFor };
}
/**
 * The rows a person sees on one page: the read filter its page rights give
 * each table. A table the person only writes to (a form) shows them their own
 * rows, and a visitor owns none. Without a page only workspace members read,
 * every row. Column releases and binding status still come from the current
 * bindings. Read-only columns come from the page's table rights, or none
 * without a page.
 */
function projectAppPageRowVisibility(input) {
    const sourceKeys = [
        ...new Set([input.binding, ...Object.values(input.bindings)].map(binding => binding.sourceKey)),
    ];
    const page = input.routePath === null
        ? null
        : (0, app_page_access_1.resolveAppPageAccess)({
            section: input.section,
            routePath: input.routePath,
            sourceKeys,
            principal: input.principal,
        });
    const rowScope = (sourceKey) => {
        if (!page)
            return input.principal.kind === 'member' ? 'all' : null;
        return (0, app_page_access_1.appPageTableRowScope)(page.tables[sourceKey], sourceKey === input.binding.sourceKey
            ? (input.operation ?? 'read')
            : 'read');
    };
    const projection = projectRowVisibility({
        binding: input.binding,
        bindings: input.bindings,
        policies: input.policies,
        anonymous: input.principal.kind === 'visitor',
        admits: admitted => rowScope(admitted.sourceKey) !== null,
        modesOf: target => [rowScope(target.sourceKey) ?? 'unavailable'],
    });
    if (!projection)
        return projection;
    return {
        ...projection,
        readOnlyColumnIds: page?.tables[input.binding.sourceKey]?.readOnlyColumns ?? [],
    };
}
/** Whether `operation` writes the bound table, decided on the page rights
 * for that write. Default: read; a scope resolved for reading never writes. */
function appPageOperationWrites(operation) {
    return operation !== undefined && operation !== 'read';
}
/** SQL and local fixtures use the same conjunction of ownership restrictions. */
function appBaseRowOwnershipRequirements(modes) {
    return { own: modes.includes('own'), group: modes.includes('group') };
}
